import { useApiClient } from '@yuksalish/api-client'
import type {
  AssignmentFilters,
  AssignmentPayload,
  BomVersionDetail,
  BomVersionSummary,
  ExecutionDashboard,
  InventoryBalanceRow,
  LotPayload,
  Machine,
  MachineAssignment,
  OrderStage,
  PagedResult,
  ProductionLot,
  ProductionOrder,
  ProductionOrderFilters,
  ProductionOrderUpdatePayload,
} from '../types/planning'

interface ApiList<T> {
  data: T[]
  meta?: {
    page?: number
    current_page?: number
    per_page?: number
    total?: number
    total_pages?: number
    last_page?: number
  }
}

interface ApiItem<T> {
  data: T
}

const MAX_PAGES = 12

/**
 * The ERP validators reject `per_page > 100` with a 422
 * ("per_page must be between 1 and 100."), so every request is clamped here.
 */
const MAX_PER_PAGE = 100

function clampPerPage(value: number | undefined, fallback: number): number {
  const requested = Number.isFinite(value) ? Number(value) : fallback
  return Math.min(Math.max(requested, 1), MAX_PER_PAGE)
}

function parseMeta(meta: ApiList<never>['meta'], fallbackPage = 1, fallbackPerPage = 20) {
  return {
    page: meta?.page ?? meta?.current_page ?? fallbackPage,
    per_page: meta?.per_page ?? fallbackPerPage,
    total: meta?.total ?? 0,
    total_pages: meta?.total_pages ?? meta?.last_page ?? 1,
  }
}

export function usePlanningApi() {
  const client = useApiClient()

  // --- Production orders -------------------------------------------------------

  async function listProductionOrders(
    filters: ProductionOrderFilters = {},
  ): Promise<PagedResult<ProductionOrder>> {
    const perPage = clampPerPage(filters.per_page, 20)
    const response = await client.get<ApiList<ProductionOrder>>('/production-orders', {
      params: {
        search: filters.search || undefined,
        status: filters.status || undefined,
        order_id: filters.order_id || undefined,
        page: filters.page ?? 1,
        per_page: perPage,
      },
    })
    return { items: response.data.data ?? [], meta: parseMeta(response.data.meta, filters.page ?? 1, perPage) }
  }

  /** Walks the order pages — the schedule board aggregates over every order. */
  async function listAllProductionOrders(
    filters: ProductionOrderFilters = {},
  ): Promise<ProductionOrder[]> {
    const items: ProductionOrder[] = []
    let page = 1
    let totalPages = 1
    do {
      const result = await listProductionOrders({ ...filters, page, per_page: 100 })
      items.push(...result.items)
      totalPages = result.meta.total_pages
      page += 1
    } while (page <= totalPages && page <= MAX_PAGES)
    return items
  }

  async function getProductionOrder(id: number): Promise<ProductionOrder> {
    const response = await client.get<ApiItem<ProductionOrder>>(`/production-orders/${id}`)
    return response.data.data
  }

  async function updateProductionOrder(
    id: number,
    payload: ProductionOrderUpdatePayload,
  ): Promise<ProductionOrder> {
    const response = await client.put<ApiItem<ProductionOrder>>(`/production-orders/${id}`, payload)
    return response.data.data
  }

  async function listOrderStages(orderId: number): Promise<OrderStage[]> {
    const response = await client.get<ApiList<OrderStage>>(`/production-orders/${orderId}/stages`)
    return response.data.data ?? []
  }

  async function listOrderLots(orderId: number): Promise<ProductionLot[]> {
    const response = await client.get<ApiList<ProductionLot>>(`/production-orders/${orderId}/lots`)
    return response.data.data ?? []
  }

  /** Batch (lot) creation — `stage_code` and `quantity` are required. */
  async function createLot(orderId: number, payload: LotPayload): Promise<ProductionLot> {
    const response = await client.post<ApiItem<ProductionLot>>(
      `/production-orders/${orderId}/lots`,
      payload,
    )
    return response.data.data
  }

  // --- Machines and assignments ------------------------------------------------

  async function listMachines(): Promise<Machine[]> {
    const response = await client.get<ApiList<Machine>>('/production-execution/machines')
    return response.data.data ?? []
  }

  async function listAssignments(
    filters: AssignmentFilters = {},
  ): Promise<PagedResult<MachineAssignment>> {
    const perPage = clampPerPage(filters.per_page, 100)
    const response = await client.get<ApiList<MachineAssignment>>(
      '/production-execution/assignments',
      {
        params: {
          shift: filters.shift || undefined,
          type: filters.type || undefined,
          order_id: filters.order_id || undefined,
          machine_id: filters.machine_id || undefined,
          employee_id: filters.employee_id || undefined,
          page: filters.page ?? 1,
          per_page: perPage,
        },
      },
    )
    return {
      items: response.data.data ?? [],
      meta: parseMeta(response.data.meta, filters.page ?? 1, perPage),
    }
  }

  /** Every assignment of one shift, used by the shift plan and machine loading. */
  async function listAllAssignments(filters: AssignmentFilters = {}): Promise<MachineAssignment[]> {
    const items: MachineAssignment[] = []
    let page = 1
    let totalPages = 1
    do {
      const result = await listAssignments({ ...filters, page, per_page: MAX_PER_PAGE })
      items.push(...result.items)
      totalPages = result.meta.total_pages
      page += 1
    } while (page <= totalPages && page <= MAX_PAGES)
    return items
  }

  async function createAssignment(payload: AssignmentPayload): Promise<MachineAssignment> {
    const response = await client.post<ApiItem<MachineAssignment>>(
      '/production-execution/assignments',
      payload,
    )
    return response.data.data
  }

  async function endAssignment(id: number): Promise<void> {
    await client.delete(`/production-execution/assignments/${id}`)
  }

  // --- Execution aggregate -----------------------------------------------------

  /** `date` and `shift` are optional; the backend defaults to the current shift. */
  async function executionDashboard(date?: string, shift?: string): Promise<ExecutionDashboard> {
    const response = await client.get<ApiItem<ExecutionDashboard>>(
      '/production-execution/dashboard',
      { params: { date: date || undefined, shift: shift || undefined } },
    )
    return response.data.data
  }

  // --- BOM and material availability ------------------------------------------

  /** BOM versions of a product (`GET /bom?product_id=…`). */
  async function listBomVersions(productId: number): Promise<BomVersionSummary[]> {
    const response = await client.get<ApiList<BomVersionSummary>>('/bom', {
      params: { product_id: productId, page: 1, per_page: 50 },
    })
    return response.data.data ?? []
  }

  async function getBomVersion(id: number): Promise<BomVersionDetail> {
    const response = await client.get<ApiItem<BomVersionDetail>>(`/bom/${id}`)
    return response.data.data
  }

  /** Stock on hand for one material, summed across warehouses by the caller. */
  async function listMaterialBalances(productId: number): Promise<InventoryBalanceRow[]> {
    const response = await client.get<ApiList<InventoryBalanceRow>>('/inventory/balances', {
      params: { product_id: productId, page: 1, per_page: 100 },
    })
    return response.data.data ?? []
  }

  return {
    listProductionOrders,
    listAllProductionOrders,
    getProductionOrder,
    updateProductionOrder,
    listOrderStages,
    listOrderLots,
    createLot,
    listMachines,
    listAssignments,
    listAllAssignments,
    createAssignment,
    endAssignment,
    executionDashboard,
    listBomVersions,
    getBomVersion,
    listMaterialBalances,
  }
}
